import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, ArrowRight, ArrowLeft, ShieldCheck, Droplet, Ban, Leaf } from 'lucide-react';
import { shopData } from './shopData';
import { useCart } from '../../../contexts/CartContext';
import { usePrice } from '../../../contexts/PriceContext';
import { useBestSellers } from '../../../utils/useBestSellers';
import { useReviewStats } from '../../../utils/useReviewStats';
import { ProductReviews } from './ProductReviews';
import { ProductInfoPanels } from './ProductInfoPanels';
import { CompleteYourStack } from './CompleteYourStack';
export function ProductDetail() {
  const {
    productId
  } = useParams();
  const navigate = useNavigate();
  const {
    addItem
  } = useCart();
  const [activeView, setActiveView] = useState<'front' | 'back'>('front');
  const bestSellers = useBestSellers();
  const reviewStats = useReviewStats();
  const product = shopData.find(p => p.id === productId);
  // Scroll to top on mount; reset gallery to the front image per product.
  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveView('front');
  }, [productId]);
  if (!product) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center" data-id="element-2226">
        <h1 className="text-3xl font-bold text-[#1F1F1F] mb-4" data-id="element-2227">
          Product Not Found
        </h1>
        <p className="text-[#5A5A5A] mb-8" data-id="element-2228">
          We couldn't find the supplement you're looking for.
        </p>
        <Link to="/shop" className="px-6 py-3 bg-[#1F1F1F] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#3B3B3B] transition-colors rounded" data-id="element-2229">
          Back to Shop
        </Link>
      </div>;
  }
  const {
    cents
  } = usePrice(product.lookupKey, product.price);
  const isBestseller = bestSellers.has(product.id);
  const galleryImages = [{
    key: 'front' as const,
    label: 'Front',
    src: product.image
  }, ...(product.backImage ? [{
    key: 'back' as const,
    label: 'Back',
    src: product.backImage
  }] : [])];
  const activeImage = activeView === 'back' && product.backImage ? product.backImage : product.image;
  const handleAdd = () => {
    if (cents === null || !product.lookupKey) return;
    addItem({
      productId: product.id,
      name: product.name,
      unitPrice: cents,
      type: 'supplement',
      lookupKey: product.lookupKey
    });
  };
  return <div className="w-full min-h-screen bg-white pt-32 md:pt-36 pb-20" data-id="element-2230">
      <div className="max-w-7xl mx-auto px-6" data-id="element-2231">
        {/* Breadcrumb */}
        <div className="text-sm text-[#5A5A5A] mb-8 md:mb-12" data-id="element-2232">
          <Link to="/shop" className="hover:text-tealslate transition-colors underline decoration-[#E5E5E5] underline-offset-4" data-id="element-2233">
            Shop All Supplements
          </Link>{' '}
          <span className="mx-2" data-id="element-2234">/</span>{' '}
          <span className="text-[#1F1F1F] font-medium" data-id="element-2235">{product.name}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start" data-id="element-2236">
          {/* LEFT: Image */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6
        }} className="flex flex-col gap-4" data-id="element-2237">
            <div className="relative aspect-square bg-[#F9F9F9] rounded-2xl border border-[#E5E5E5] flex items-center justify-center p-12 lg:p-20" data-id="element-2238">
              {isBestseller && <span className="absolute top-6 left-6 z-10 px-3 py-1.5 bg-[#1F73B7] text-white text-xs font-bold tracking-wide rounded-sm shadow-sm" data-id="element-2239">
                  Best Seller
                </span>}
              <img src={activeImage} alt={`${product.name} — ${activeView} of bottle`} className="w-full h-full object-contain drop-shadow-xl" data-id="element-2240" />
            </div>

            {galleryImages.length > 1 && <div className="flex items-center gap-3 pb-5" role="group" aria-label="Product images" data-id="element-2241">
                {galleryImages.map(g => <button key={g.key} type="button" onClick={() => setActiveView(g.key)} aria-pressed={activeView === g.key} aria-label={`View ${g.label} of bottle`} className={`relative w-20 h-20 rounded-lg border-2 bg-[#F9F9F9] flex items-center justify-center p-2 transition-colors ${activeView === g.key ? 'border-[#1F1F1F]' : 'border-[#E5E5E5] hover:border-[#9A9A90]'}`} data-id="element-2242">
                    <img src={g.src} alt={`${product.name} ${g.label}`} className="w-full h-full object-contain" data-id="element-2243" />
                    <span className="absolute -bottom-5 left-0 right-0 text-center text-[10px] font-medium uppercase tracking-wider text-[#9A9A90]" data-id="element-2244">
                      {g.label}
                    </span>
                  </button>)}
              </div>}
          </motion.div>

          {/* RIGHT: Details */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="flex flex-col font-shop" data-id="element-2245">
            {/* Title & Rating */}
            <h1 className="text-4xl md:text-5xl font-bold text-[#1F1F1F] mb-4 tracking-tight leading-tight" data-id="element-2246">
              {product.name}
            </h1>

            <div className="flex items-center gap-1 mb-6" data-id="element-2247">
              {[1, 2, 3, 4, 5].map(star => <Star key={star} className={`w-4 h-4 ${star <= Math.round(reviewStats[product.id]?.avg || 0) ? 'fill-[#1F73B7] text-[#1F73B7]' : 'text-[#D8D8D0]'}`} data-id="element-2248" />)}
              <a href="#reviews" className="text-sm text-[#5A5A5A] hover:text-[#1F1F1F] underline decoration-[#E5E5E5] underline-offset-4 ml-2 transition-colors" data-id="element-2249">
                {reviewStats[product.id]?.count || 0} Reviews
              </a>
            </div>

            {/* Description */}
            <p className="text-lg text-[#3B3B3B] leading-relaxed mb-6" data-id="element-2250">
              {product.shortDescription}
            </p>

            <a href="#" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1F73B7] hover:text-[#155A92] transition-colors mb-8" data-id="element-2251">
              View Ingredient Testing & Quality{' '}
              <ArrowRight className="w-4 h-4" data-id="element-2252" />
            </a>

            {/* Badges */}
            <div className="grid grid-cols-2 gap-y-4 gap-x-6 mb-10" data-id="element-2253">
              <div className="flex items-center gap-3 text-sm text-[#1F1F1F] font-medium" data-id="element-2254">
                <Ban className="w-5 h-5 text-[#9A9A90]" data-id="element-2255" />
                No Artificial Sweeteners
              </div>
              <div className="flex items-center gap-3 text-sm text-[#1F1F1F] font-medium" data-id="element-2256">
                <Droplet className="w-5 h-5 text-[#9A9A90]" data-id="element-2257" />
                No Artificial Coloring
              </div>
              <div className="flex items-center gap-3 text-sm text-[#1F1F1F] font-medium" data-id="element-2258">
                <ShieldCheck className="w-5 h-5 text-[#9A9A90]" data-id="element-2259" />
                No Artificial Preservatives
              </div>
              <div className="flex items-center gap-3 text-sm text-[#1F1F1F] font-medium" data-id="element-2260">
                <Leaf className="w-5 h-5 text-[#9A9A90]" data-id="element-2261" />
                No Fillers
              </div>
            </div>

            {/* Serving Size */}
            <div className="mb-8" data-id="element-2262">
              <p className="text-sm text-[#5A5A5A] mb-2" data-id="element-2263">Serving Size:</p>
              <div className="inline-flex items-center justify-center px-6 py-3 border border-[#1F1F1F] rounded text-sm font-bold text-[#1F1F1F]" data-id="element-2264">
                30 Servings
              </div>
            </div>

            {/* Purchase Panel */}
            <div className="bg-[#F9F9F9] border border-[#E5E5E5] rounded-xl p-6 mb-8" data-id="element-2265">
              <div className="flex items-center justify-between mb-6" data-id="element-2266">
                <div className="flex items-center gap-3" data-id="element-2267">
                  <div className="w-5 h-5 rounded-full border-4 border-[#1F1F1F] bg-white" data-id="element-2268" />
                  <span className="text-base font-bold text-[#1F1F1F]" data-id="element-2269">
                    One-Time Purchase
                  </span>
                </div>
                <span className="text-xl font-bold text-[#1F1F1F]" data-id="element-2270">
                  {cents !== null ? `$${(cents / 100).toFixed(2)}` : '—'}
                </span>
              </div>

              <button onClick={handleAdd} disabled={cents === null || !product.lookupKey} className="w-full py-4 bg-[#1F1F1F] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#3B3B3B] transition-colors disabled:opacity-40 rounded shadow-sm" data-id="element-2271">
                Add to Cart
              </button>
            </div>

            {/* Accordion Details */}
            <details className="group border-t border-[#E5E5E5]" data-id="element-2272">
              <summary className="cursor-pointer list-none flex items-center justify-between py-5 text-base font-bold text-[#1F1F1F]" data-id="element-2273">
                Why this dose?
                <span className="text-2xl font-light leading-none group-open:rotate-45 transition-transform origin-center" data-id="element-2274">
                  +
                </span>
              </summary>
              <p className="text-base text-[#5A5A5A] leading-relaxed pb-6" data-id="element-2275">
                {product.whyEffective}
              </p>
            </details>
            <details className="group border-t border-b border-[#E5E5E5]" data-id="element-2276">
              <summary className="cursor-pointer list-none flex items-center justify-between py-5 text-base font-bold text-[#1F1F1F]" data-id="element-2277">
                How to use
                <span className="text-2xl font-light leading-none group-open:rotate-45 transition-transform origin-center" data-id="element-2278">
                  +
                </span>
              </summary>
              <p className="text-base text-[#5A5A5A] leading-relaxed pb-6" data-id="element-2279">
                Take {product.dose} {product.doseUnit} daily for optimal
                results. Consistency is key.
              </p>
            </details>
          </motion.div>
        </div>
      </div>

      <ProductInfoPanels product={product} data-id="element-2280" />

      <CompleteYourStack product={product} data-id="element-2281" />

      <ProductReviews productId={product.id} data-id="element-2282" />
    </div>;
}